import { Component, inject, OnInit, signal } from "@angular/core";
import { SidenavComponent } from "../../core/components/sidenav/sidenav.component";
import { HeaderComponent } from "../../core/components/header/header.component";
import { Router, RouterModule } from "@angular/router";
import { AiChatComponent } from "../../core/components/ai-chat/ai-chat.component";
import { SvgIconComponent } from "angular-svg-icon";
import { CommunicationService } from "../../core/services/communication/communication.service";
import { UserService } from "../../core/services/user/user.service";
import { UsersPrivilegeKey } from "../../core/models/user";
import { ErrorDialogComponent } from "../../core/components/error-dialog/error-dialog.component";
import { SessionService } from "../../core/services/session.service";
import { CognitoService } from "../../core/services/cognito.service";
import { JwtHelperService } from "@auth0/angular-jwt";
import { interval, of, Subject, switchMap, takeUntil } from "rxjs";
import { ToastModule } from "primeng/toast";
import { CommonModule } from "@angular/common";
import { SharedService } from "../../core/services/shared/shared.service";
import { ToastService } from "../../core/services/toast.service";
@Component({
    selector: "app-layout",
    imports: [SidenavComponent, HeaderComponent, RouterModule, AiChatComponent, SvgIconComponent, ErrorDialogComponent, ToastModule, CommonModule],
    templateUrl: "./layout.component.html",
    styleUrl: "./layout.component.scss"
})
export class LayoutComponent implements OnInit {
    private readonly eventService = inject(CommunicationService);
    private readonly cognitoService = inject(CognitoService);
    private readonly userService = inject(UserService);
    private readonly sessionService = inject(SessionService);
    private readonly jwtHelperService = inject(JwtHelperService);
    private readonly router = inject(Router);
    private readonly sharedService = inject(SharedService);
    private readonly toastService = inject(ToastService);
    protected readonly isAiChatEnabled = signal(false);
    protected readonly sessionDialogTitle = $localize`:@@session.dialog.title:Your session will expire in 2 minutes`;
    protected readonly sessionDialogContent = $localize`:@@session.dialog.content:Do you want to continue?`;
    protected readonly sessionDialogHeader = $localize`:@@session.dialog.heading:Session Expiring`;
    protected readonly cancelBttnText = $localize`:@@session.dialog.btn.sign.out:Sign Out`;
    protected readonly btnName = $localize`:@@session.ok.btn:Extend`;
    protected readonly idleTimeDialogVisible = signal(false);
    protected readonly aiChatVisible = signal(false);
    private readonly stopAccessTokenCheck = new Subject<void>();

    ngOnInit() {
        this.sessionService.idleTimeEvent$.subscribe({
            next: () => {
                this.idleTimeDialogVisible.set(true);
            },
        });
        this.sessionService.timeoutEvent$.subscribe({
            next: () => {
                this.signOut();
            },
        });

        //TODO move to a service
        interval(10000)
            .pipe(
                switchMap(() => {
                    const token = localStorage.getItem("accessToken");
                    if (!token) {
                        return of(this.signOut());
                    }
                    if (this.jwtHelperService.isTokenExpired(token)) {
                        return of(this.signOut());
                    } else {
                        const expirationDate = this.jwtHelperService.getTokenExpirationDate(token);
                        if (!expirationDate) {
                            return of(this.signOut());
                        } else {
                            const timeLeft = expirationDate.getTime() - Date.now();
                            if (timeLeft <= 60000) {
                                return this.cognitoService.refreshSession();
                            }
                        }
                    }
                    return of();
                }),
                takeUntil(this.stopAccessTokenCheck),
            )
            .subscribe({
                next: () => {},
                error: (err) => {
                    console.error("Error in sessionInterval subscription:", err);
                },
            });

        this.sessionService.startSession();

        if (this.userService.isAccessNone(UsersPrivilegeKey.Help_Center_Connie)) return;

        this.isAiChatEnabled.set(true);
        this.eventService.openChatEvent$.subscribe({
            next: () => {
                this.aiChatVisible.set(true);
            },
            error: (err) => {
                console.error("Error opening chat:", err);
            },
        });
    }

    protected extendSession() {
        this.sessionService.extendSession();
        this.idleTimeDialogVisible.set(false);
    }

    protected signOut() {
        this.cognitoService.signOut().subscribe({
            next: () => {
                this.userService.resetUserPrivileges();
                this.stopAccessTokenCheck.next();
                this.clearToken();
                this.router.navigate(["auth/signin"]);
            },
            error: (err) => {
                console.error("Error during sign out:", err);
            },
        });
    }

    private clearToken(){
		localStorage.removeItem("idToken");
		localStorage.removeItem("accessToken");
		localStorage.removeItem("refreshToken");
		localStorage.removeItem("userState");
    }

    gotoNavigate(navigation:string, isExternal?: boolean) {
        if(navigation === 'publisher'){
		    this.sharedService.openPanel();
        } else {
            if(isExternal){
                window.open(navigation, '_blank', 'noopener,noreferrer');
            } else {
                this.router.navigate([navigation]);
            }
        }
        this.toastService.closeCustomToast();
    }   
    
}
